const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';
const GOOGLE_SUBSCRIPTIONS_URL = 'https://play.google.com/store/account/subscriptions';

const RECOVERY_STATUSES = new Set(['past_due', 'grace_period', 'paused']);

export function normalizeSubscriptionProvider(provider) {
  const normalized = typeof provider === 'string' ? provider.trim().toLowerCase() : '';
  return ['stripe', 'apple', 'google', 'manual', 'crypto'].includes(normalized) ? normalized : 'unknown';
}

export function normalizeSubscriptionStatus(status) {
  const normalized = typeof status === 'string' ? status.trim().toLowerCase() : 'none';
  if (normalized === 'grace') return 'grace_period';
  if (normalized === 'needs_reconciliation') return 'past_due';
  if (normalized === 'cancelled') return 'cancel_at_period_end';
  return normalized || 'none';
}

/**
 * Resolve where the current subscription may be managed. Only an explicitly
 * Stripe-backed subscription is allowed to request a Stripe portal session.
 */
export function getSubscriptionManagementTarget(subscription) {
  const provider = normalizeSubscriptionProvider(subscription?.provider);

  if (provider === 'stripe') {
    return { kind: 'stripe', provider, url: null };
  }
  if (provider === 'apple') {
    return { kind: 'external', provider, url: APPLE_SUBSCRIPTIONS_URL };
  }
  if (provider === 'google') {
    return { kind: 'external', provider, url: GOOGLE_SUBSCRIPTIONS_URL };
  }
  return { kind: 'none', provider, url: null };
}

export function canUseStripePortal(subscription) {
  return getSubscriptionManagementTarget(subscription).kind === 'stripe';
}

export function hasSubscriptionRecoveryStatus(subscription) {
  return RECOVERY_STATUSES.has(normalizeSubscriptionStatus(subscription?.status));
}

export function formatSubscriptionDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  }).format(date);
}

export function getSubscriptionTierLabel(subscriptionOrTier) {
  const tier = typeof subscriptionOrTier === 'object' ? subscriptionOrTier?.tier : subscriptionOrTier;
  if (tier === 'unlimited_pro') return 'Unlimited Pro';
  if (!tier || tier === 'unlimited') return 'Unlimited';
  return String(tier)
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function managementLabel(management, recovery) {
  if (management.provider === 'apple') {
    return recovery ? 'Update billing in App Store' : 'Manage in App Store';
  }
  if (management.provider === 'google') {
    return recovery ? 'Update billing in Google Play' : 'Manage in Google Play';
  }
  if (management.provider === 'stripe') {
    return recovery ? 'Update payment method' : 'Manage subscription';
  }
  return null;
}

function recoveryInstruction(management) {
  if (management.provider === 'apple') return 'Update billing in your App Store account to restore access.';
  if (management.provider === 'google') return 'Update billing in Google Play to restore access.';
  if (management.provider === 'stripe') return 'Update your payment method to restore access.';
  if (management.provider === 'manual') return 'Contact Sogni support to restore access.';
  return 'Contact the provider where you purchased the subscription to restore access.';
}

function pausedInstruction(management) {
  if (management.provider === 'apple') return 'Manage it in the App Store to review or resume it.';
  if (management.provider === 'google') return 'Manage it in Google Play to review or resume it.';
  if (management.provider === 'stripe') return 'Manage your subscription to review or resume it.';
  if (management.provider === 'manual') return 'Contact Sogni support to review or resume it.';
  return 'Contact the provider where you purchased it to review or resume it.';
}

function billingFootnote(provider, showCurrent) {
  if (!showCurrent || provider === 'stripe') {
    return 'Billing is handled securely by Stripe on Sogni. Manage or cancel anytime.';
  }
  if (provider === 'apple') {
    return 'Billing is handled by Apple. Manage or cancel in your App Store account.';
  }
  if (provider === 'google') {
    return 'Billing is handled by Google Play. Manage or cancel in Google Play.';
  }
  if (provider === 'manual') return 'This subscription is managed directly by Sogni.';
  if (provider === 'crypto') return 'This plan was paid with crypto and does not renew automatically.';
  return 'Manage this subscription through the provider where it was purchased.';
}

/**
 * Convert the provider-neutral SDK snapshot into display and action state for
 * the subscription modal. The snapshot's `active` field remains authoritative;
 * `isUnlimited` is only a compatibility fallback for older snapshots.
 */
export function getSubscriptionViewModel(subscription, { isUnlimited = false } = {}) {
  const status = normalizeSubscriptionStatus(subscription?.status);
  const provider = normalizeSubscriptionProvider(subscription?.provider);
  const active = typeof subscription?.active === 'boolean'
    ? subscription.active
    : Boolean(isUnlimited);
  const recovery = RECOVERY_STATUSES.has(status);
  const trial = status === 'trialing';
  const ending = status === 'cancel_at_period_end' || subscription?.cancelAtPeriodEnd === true;
  const showCurrent = active || recovery;
  const management = showCurrent
    ? getSubscriptionManagementTarget(subscription)
    : { kind: 'none', provider, url: null };
  const name = getSubscriptionTierLabel(subscription);
  const periodEnd = formatSubscriptionDate(subscription?.currentPeriodEnd);
  const recoveryEnd = formatSubscriptionDate(subscription?.graceEnd || subscription?.currentPeriodEnd);

  let badgeLabel = null;
  let badgeTone = 'success';
  let description = null;

  if (recovery) {
    badgeLabel = status === 'paused' ? 'Subscription paused' : 'Payment needs attention';
    badgeTone = status === 'paused' ? 'warning' : 'error';
    if (status === 'grace_period') {
      description = `Your renewal payment is being retried, so ${name} access is paused until payment succeeds. ${recoveryInstruction(management)}`;
      if (recoveryEnd) description += ` The retry window ends ${recoveryEnd}.`;
    } else if (status === 'past_due') {
      description = `A subscription payment is past due, so ${name} access is paused. ${recoveryInstruction(management)}`;
    } else {
      description = `Your ${name} subscription is paused. ${pausedInstruction(management)}`;
    }
  } else if (trial) {
    badgeLabel = ending ? 'Free trial ending' : 'Free trial active';
    badgeTone = ending ? 'warning' : 'info';
    if (ending) {
      description = periodEnd
        ? `Your ${name} trial will not renew and access ends ${periodEnd}.`
        : `Your ${name} trial will not renew.`;
    } else {
      description = periodEnd
        ? `You're on a free trial of ${name} · trial ends ${periodEnd}.`
        : `You're on a free trial of ${name}.`;
    }
  } else if (ending) {
    badgeLabel = `${name} ending`;
    badgeTone = 'warning';
    description = periodEnd
      ? `You're on ${name}. Access remains active until ${periodEnd} and will not renew.`
      : `You're on ${name}. Access remains active through the current period and will not renew.`;
  } else if (active) {
    badgeLabel = `✓ ${name} active`;
    description = provider === 'crypto'
      ? periodEnd ? `You're on ${name} · access ends ${periodEnd}. This plan does not renew automatically.` : `You're on ${name}. This plan does not renew automatically.`
      : periodEnd ? `You're on ${name} · renews ${periodEnd}.` : `You're on ${name}.`;
  }

  let managementNote = null;
  if (showCurrent && management.kind === 'none') {
    managementNote = provider === 'manual'
      ? 'This subscription is managed by Sogni support.'
      : provider === 'crypto'
        ? 'Crypto plans are one-time purchases. Choose a future plan from your Sogni dashboard.'
        : 'Subscription management is not available in Vibe for this billing provider.';
  }

  return {
    name,
    tier: subscription?.tier || null,
    active,
    status,
    provider,
    recovery,
    trial,
    ending,
    showCurrent,
    badgeLabel,
    badgeTone,
    description,
    management,
    managementLabel: managementLabel(management, recovery && status !== 'paused'),
    managementNote,
    footnote: billingFootnote(provider, showCurrent)
  };
}
